import type { EditorInstance } from "@quire/editor";
import { useEffect, useRef, useState } from "preact/hooks";
import type { App } from "../app.ts";
import { getEditor } from "../editors/index.ts";
import { isKeptImage } from "../lib/images.ts";
import { useAppState } from "./hooks.ts";
import type { NewMode } from "./new-form.tsx";

const SAVE_TEXT = {
  idle: "",
  saving: "Saving…",
  saved: "Saved",
  unsaved: "Unsaved changes",
  error: "Not saved",
  conflict: "Needs your choice",
} as const;

interface Props {
  app: App;
  onEditor: (editor: EditorInstance | null) => void;
  begin: (mode: Exclude<NewMode, null>) => void;
}

export function Sheet({ app, onEditor, begin }: Props) {
  const s = useAppState(app);
  const host = useRef<HTMLDivElement>(null);

  // The editor is rebuilt only when the kind changes; text carries over.
  useEffect(() => {
    let cancelled = false;
    let instance: EditorInstance | null = null;
    Promise.resolve(
      getEditor(s.editorId).create(host.current!, {
        value: "",
        placeholder: "Begin writing…",
        onChange: (v) => app.onEditorChange(v),
        onCaret: (f) => app.setCaret(f),
        images: {
          onFile: (f) => app.addImage(f),
          load: (p) => app.readImage(p),
          handles: isKeptImage,
        },
        onLink: (address) => app.followLink(address),
        onPrompt: (id) => window.dispatchEvent(new CustomEvent("quire:prompt", { detail: id })),
      }),
    ).then((created) => {
      if (cancelled) {
        created.destroy();
        return;
      }
      instance = created;
      app.attachEditor(created);
      onEditor(created);
    });
    return () => {
      cancelled = true;
      onEditor(null);
      instance?.destroy();
    };
  }, [s.editorId]);

  const has = Boolean(s.current);
  const words = `${s.wordCount} ${s.wordCount === 1 ? "word" : "words"}`;
  const chars = `${s.charCount} ${s.charCount === 1 ? "character" : "characters"}`;
  const save = SAVE_TEXT[s.save] + (s.saveDetail ? `. ${s.saveDetail}` : "");

  return (
    <main id="sheet" class="sheet" tabIndex={-1}>
      <div class="sheet-name" hidden={!has}>
        {s.current && s.current.title && s.current.title !== s.current.path && (
          <span class="sheet-title">{s.current.title}</span>
        )}
        {s.current && <FileName app={app} path={s.current.path} request={s.renameRev} />}
      </div>
      <div class="editor-host" ref={host} hidden={!has} />
      {!has && s.ready && (
        <div class="empty">
          <h1 class="empty-title">A blank page</h1>
          <p class="muted">Open a file from the sidebar, or begin something new.</p>
          <div class="row center">
            <button class="button primary" type="button" onClick={() => begin("thought")}>
              New thought
            </button>
            <button class="button" type="button" onClick={() => begin("document")}>
              New document
            </button>
          </div>
        </div>
      )}
      <p class="counts" hidden={!has} data-state={s.save}>
        <span role="status" aria-live="polite">
          {save}
        </span>
        {save && " · "}
        {words} · {chars}
      </p>
    </main>
  );
}

/** The file's name above the note. Pressing it turns it into a box to type a new name. */
function FileName({ app, path, request }: { app: App; path: string; request: number }) {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // The palette can ask for the box to open.
  const seen = useRef(request);
  useEffect(() => {
    if (request === seen.current) return;
    seen.current = request;
    setEditing(true);
  }, [request]);
  // A new file, or a finished rename, closes the box.
  useEffect(() => {
    setEditing(false);
    setError("");
  }, [path]);
  useEffect(() => {
    const el = input.current;
    if (!editing || !el) return;
    el.focus();
    // The name without its .md is selected, so typing replaces just that.
    const dot = path.lastIndexOf(".");
    el.setSelectionRange(0, dot > 0 ? dot : path.length);
  }, [editing]);

  if (!editing) {
    return (
      <button
        class="name-button"
        type="button"
        title="Rename the file"
        aria-label={`Rename ${path}`}
        onClick={() => setEditing(true)}
      >
        {path}
      </button>
    );
  }
  const close = () => {
    setEditing(false);
    setError("");
  };
  return (
    <form
      class="name-form"
      onSubmit={async (e) => {
        e.preventDefault();
        const value = input.current?.value.trim() ?? "";
        if (!value) return setError("Give the file a name.");
        setBusy(true);
        const result = await app.renameCurrent(value);
        setBusy(false);
        if (result.ok) close();
        else setError(result.message ?? "");
      }}
    >
      <label class="visually-hidden" for="file-name">
        File name
      </label>
      <input
        id="file-name"
        ref={input}
        class="field"
        type="text"
        defaultValue={path}
        autocomplete="off"
        spellcheck={false}
        aria-invalid={error !== ""}
        aria-describedby={error ? "file-name-error" : undefined}
        onInput={() => setError("")}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            close();
          }
        }}
      />
      <button class="button primary" type="submit" disabled={busy}>
        Rename
      </button>
      <button class="button" type="button" onClick={close}>
        Cancel
      </button>
      {error && (
        <p id="file-name-error" class="form-error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
